// 飘字类型
export type FloatingTextType = 'coin' | 'cooldown' | 'crit' | 'combo' | 'achievement' | 'exp' | 'info';

// 飘字实例
interface FloatingText {
  x: number;
  y: number;
  text: string;
  type: FloatingTextType;
  startTime: number;
  duration: number;
  startY: number;
  scale: number;
  opacity: number;
}

export class FloatingTextSystem {
  private texts: FloatingText[] = [];
  private pool: FloatingText[] = [];
  
  // 配置
  private config: Record<FloatingTextType, {
    color: string;
    fontSize: number;
    duration: number;
    riseHeight: number;
    strokeColor: string;
  }> = {
    coin: {
      color: '#FFD700',
      fontSize: 24,
      duration: 1200,
      riseHeight: 60,
      strokeColor: '#B8860B',
    },
    cooldown: {
      color: '#AAAAAA',
      fontSize: 16,
      duration: 800,
      riseHeight: 30,
      strokeColor: '#666666',
    },
    crit: {
      color: '#FF4444',
      fontSize: 28,
      duration: 1500,
      riseHeight: 80,
      strokeColor: '#8B0000',
    },
    combo: {
      color: '#FF8C00',
      fontSize: 26,
      duration: 1400,
      riseHeight: 70,
      strokeColor: '#CC6600',
    },
    achievement: {
      color: '#FFD700',
      fontSize: 20,
      duration: 2000,
      riseHeight: 50,
      strokeColor: '#B8860B',
    },
    exp: {
      color: '#4CAF50',
      fontSize: 18,
      duration: 1000,
      riseHeight: 40,
      strokeColor: '#2E7D32',
    },
    info: {
      color: '#2196F3',
      fontSize: 16,
      duration: 1500,
      riseHeight: 35,
      strokeColor: '#1565C0',
    },
  };
  
  spawn(x: number, y: number, text: string, type: FloatingTextType = 'coin'): void {
    let floatingText: FloatingText;
    
    // 从对象池获取或创建新实例
    if (this.pool.length > 0) {
      floatingText = this.pool.pop()!;
      floatingText.x = x;
      floatingText.y = y;
      floatingText.text = text;
      floatingText.type = type;
      floatingText.startTime = Date.now();
      floatingText.startY = y;
      floatingText.scale = 0.5;
      floatingText.opacity = 1;
    } else {
      floatingText = {
        x,
        y,
        text,
        type,
        startTime: Date.now(),
        duration: this.config[type].duration,
        startY: y,
        scale: 0.5,
        opacity: 1,
      };
    }
    
    this.texts.push(floatingText);
  }
  
  update(_dt: number): void {
    const now = Date.now();
    
    this.texts = this.texts.filter(text => {
      const elapsed = now - text.startTime;
      const progress = elapsed / text.duration;
      
      if (progress >= 1) {
        // 回收对象
        this.pool.push(text);
        return false;
      }
      
      // 更新位置（上升）
      const config = this.config[text.type];
      const easeOut = 1 - (1 - progress) * (1 - progress);
      text.y = text.startY - config.riseHeight * easeOut;
      
      // 更新缩放（先放大后稳定）
      if (progress < 0.2) {
        text.scale = 0.5 + 0.7 * (progress / 0.2);
      } else if (progress < 0.4) {
        text.scale = 1.2 - 0.2 * ((progress - 0.2) / 0.2);
      } else {
        text.scale = 1;
      }
      
      // 更新透明度（最后淡出）
      if (progress > 0.7) {
        text.opacity = 1 - (progress - 0.7) / 0.3;
      } else {
        text.opacity = 1;
      }
      
      return true;
    });
  }
  
  render(ctx: CanvasRenderingContext2D): void {
    this.texts.forEach(text => {
      this.renderText(ctx, text);
    });
  }
  
  private renderText(ctx: CanvasRenderingContext2D, text: FloatingText): void {
    const config = this.config[text.type];
    
    ctx.save();
    ctx.globalAlpha = text.opacity;
    
    // 设置字体
    const fontSize = config.fontSize * text.scale;
    ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // 绘制描边
    ctx.strokeStyle = config.strokeColor;
    ctx.lineWidth = 3;
    ctx.strokeText(text.text, text.x, text.y);
    
    // 绘制文字
    ctx.fillStyle = config.color;
    ctx.fillText(text.text, text.x, text.y);
    
    // 如果是金币类型，绘制金币图标
    if (text.type === 'coin') {
      const iconSize = fontSize * 0.8;
      const textWidth = ctx.measureText(text.text).width;
      const iconX = text.x - textWidth / 2 - iconSize;
      const iconY = text.y - iconSize / 2;
      
      // 绘制小金币
      ctx.fillStyle = '#FFD700';
      ctx.beginPath();
      ctx.arc(iconX + iconSize / 2, iconY + iconSize / 2, iconSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#B8860B';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // 金币上的符号
      ctx.fillStyle = '#B8860B';
      ctx.font = `bold ${iconSize * 0.6}px serif`;
      ctx.fillText('¥', iconX + iconSize / 2, iconY + iconSize / 2 + 1);
    }
    
    ctx.restore();
  }
  
  // 获取活跃飘字数量（用于调试）
  getActiveCount(): number {
    return this.texts.length;
  }
  
  // 获取对象池大小（用于调试）
  getPoolSize(): number {
    return this.pool.length;
  }
}
